import React, { useState } from 'react';
import styled from 'styled-components';
import CheckBoxDisable from '@/assets/cs/icon_checkbox_disabled.png';
import CheckBoxEnable from '@/assets/cs/icon_checkbox_enabled.png';

type RadioOption = {
  id: string;
  label: string;
  value: string;
};

type RadioSectionProps = {
  options: RadioOption[];
  name: string;
  title?: string;
  isRequired?: boolean;
  onChange?: (value: string) => void;
  defaultValue?: string;
};

function RadioSection({
  options,
  name,
  title,
  isRequired = false,
  onChange,
  defaultValue,
}: RadioSectionProps) {
  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue || '',
  );

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedValue(value);

    if (onChange) {
      onChange(value);
    }
  };

  return (
    <div>
      {title && (
        <RadioTitle>
          {title} {isRequired && <span>*</span>}
        </RadioTitle>
      )}
      <RadioContainer>
        {options.map((option) => (
          <RadioItem
            key={option.id}
            onClick={() => {
              const value = option.value;
              setSelectedValue(value);
              if (onChange) {
                onChange(value);
              }
            }}
          >
            <HiddenRadioInput
              type='radio'
              id={option.id}
              name={name}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={handleRadioChange}
            />
            <CustomRadio checked={selectedValue === option.value}>
              {selectedValue === option.value ? (
                <CheckboxIcon src={CheckBoxEnable} alt='Checked' />
              ) : (
                <CheckboxIcon src={CheckBoxDisable} alt='Unchecked' />
              )}
              <RadioLabel
                htmlFor={option.id}
                checked={selectedValue === option.value}
              >
                {option.label}
              </RadioLabel>
            </CustomRadio>
          </RadioItem>
        ))}
      </RadioContainer>
    </div>
  );
}

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
`;

const RadioTitle = styled.div`
  color: var(--Mono-65, #5f6165);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
  margin-bottom: 6px;
  > span {
    color: var(--Secondary-Danger);
  }
`;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-start;
  cursor: pointer;
`;

const HiddenRadioInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const CustomRadio = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  width: auto;
  height: 26px;

  padding: 1px 6px;
  border-radius: 20px;
  transition: all 0.2s ease;
  border: 1px solid ${({ checked }) => (checked ? '#246CF6' : '#e5e6e8')};
  background-color: ${({ checked }) => (checked ? '#246CF6' : '#FFF')};
`;

const RadioLabel = styled.label<{ checked: boolean }>`
  cursor: pointer;
  margin-left: 8px;
  font-size: 14px;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
  color: ${({ checked }) => (checked ? '#FFF' : '#A7A7A7')}!important;
`;

const CheckboxIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

export default RadioSection;
